import { UseQueryResult } from '@tanstack/react-query';
import SharedGpanelContactsGrid from '../../shared/components/shared-gpanel-contacts-grid';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import TooltipWithPermission from '../../shared/components/tooltip-with-permission';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import { GpanelContactsGridToolbarProps } from '../gpanel-contacts/gpanel-contacts-grid/gpanel-contacts-grid-toolbar';
import SharedGridStateIconButton from '../../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { Messages } from '../../../../../utils/message-constants';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Label from '@mui/icons-material/Label';
import Refresh from '@mui/icons-material/Refresh';
import Restore from '@mui/icons-material/Restore';

type Permission = {
  name: string;
  value: boolean;
};

export default function GpanelContactsTrashPage<T extends GridValidRowModel>({
  ...props
}: {
  query: UseQueryResult<T[], Error>;
  title: string;
  columns: GridColDef<T>[];
  recoverPermission: Permission;
  emptyPermission: Permission;
  onRecover: (model: number[]) => void;
  onEmpty: () => void;
  gridKey?: string | null;
}) {
  const {
    query,
    title,
    recoverPermission,
    onRecover,
    emptyPermission,
    onEmpty,
    columns,
  } = props;
  const [selection, setSelection] = useState<number[]>([]);

  function TrashedItemsToolbar({
    selection,
    onRefresh,
  }: GpanelContactsGridToolbarProps) {
    return (
      <SharedToolbar>
        <Tooltip
          arrow
          title={
            recoverPermission.value
              ? recoverPermission.name
              : Messages.Errors.Permission.Acting(recoverPermission.name)
          }
        >
          <span>
            <IconButton
              onClick={() => onRecover(selection)}
              disabled={!recoverPermission.value || !selection?.length}
            >
              <Restore />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip arrow title="Refresh">
          <span>
            <IconButton onClick={onRefresh}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
        <SharedGridStateIconButton
          gridStateKey={
            title === 'Labels'
              ? 'trashedContactLabelsGridStates'
              : 'trashedContactsGridStates'
          }
          module={ModuleList.modules.directory.name}
          section={ModuleList.modules.directory.sections.gpanelContacts}
        />
      </SharedToolbar>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{ position: 'relative', overflow: 'hidden', height: '100%' }}
    >
      {Boolean(query.data?.length) && (
        <Alert
          severity="info"
          action={
            <TooltipWithPermission
              hasPermission={emptyPermission.value}
              permissionName={emptyPermission.name}
              renderElement={(hasPermission) => (
                <Button
                  color="info"
                  disabled={!hasPermission}
                  onClick={() => onEmpty()}
                >
                  empty trash now
                </Button>
              )}
            />
          }
          sx={{
            position: 'relative',
            flexShrink: 0,
            '& .MuiAlert-action': {
              p: 0,
            },
          }}
        >{`${title} that have been in Trash more than 30 days will be deleted forever.`}</Alert>
      )}
      <div style={{ overflow: 'hidden', flexGrow: 1 }}>
        <SharedGpanelContactsGrid
          {...props}
          columns={columns}
          onRefresh={() => query.refetch()}
          toolbar={TrashedItemsToolbar as never}
          selection={selection}
          disableRowClick
          onSelection={setSelection}
          title={'Trash - ' + title}
          {...createGridOverlays(title, <Label />, ' ', `No ${title} Found`)}
          gridStateKey={
            title === 'Labels'
              ? 'trashedContactLabelsGridStates'
              : 'trashedContactsGridStates'
          }
        />
      </div>
    </Stack>
  );
}
